import { Menu } from "@headlessui/react";
import CaretDropdown from "../ReusableComponents/CaretDropdown";
import {
    type DashboardAlertsData,
    DashboardCategories,
    type DashboardCategoriesType,
    type DashboardContentSections,
} from "../../models/api/admin/dashboard";
import {
    Car,
    CheckCircle,
    IdentificationCard,
    Scroll,
    Truck,
} from "phosphor-react";
import { useDashboardAlertActions } from "./useDashboardAlertActions";
import DashboardAlertsTabContents from "./DashboardAlertsTabContents";
import {
    getDashboardTabActions,
    getDashboardTabData,
} from "../../utils/dashboardTabUtils";
import LoadingMessage from "../ReusableComponents/LoadingMessage/LoadingMessage";

type DropDownContentSelectorProps = {
    selectedTab: DashboardCategoriesType;
    setSelectedTab: React.Dispatch<
        React.SetStateAction<DashboardCategoriesType>
    >;
    isLoading: boolean;
    alertsData?: DashboardAlertsData;
    actions: ReturnType<typeof useDashboardAlertActions>;
};

function DropDownContentSelector({
    selectedTab,
    setSelectedTab,
    isLoading,
    alertsData,
    actions,
}: DropDownContentSelectorProps) {
    const handleSelect = (tab: DashboardCategoriesType) => {
        setSelectedTab(tab);
    };

    const options: Array<{
        id: DashboardCategoriesType;
        title: DashboardContentSections;
        icon: JSX.Element;
    }> = [
        {
            id: DashboardCategories.Drivers,
            title: "All Drivers",
            icon: <IdentificationCard size={24} weight="duotone" />,
        },
        {
            id: DashboardCategories.ProfessionalDrivers,
            title: "Professional Drivers",
            icon: <Truck size={24} weight="duotone" />,
        },
        {
            id: DashboardCategories.Dvla,
            title: "DVLA Checks",
            icon: <CheckCircle size={24} weight="duotone" />,
        },
        {
            id: DashboardCategories.GreyFleet,
            title: "Grey Fleet",
            icon: <Car size={24} weight="duotone" />,
        },
        {
            id: DashboardCategories.DriverDeclarations,
            title: "Driver Declarations",
            icon: <Scroll size={24} weight="duotone" />,
        },
    ];

    return (
        <div className="mb-4">
            {isLoading ? (
                <LoadingMessage />
            ) : (
                <>
                    <Menu>
                        {({ open }) => (
                            <>
                                <Menu.Button className="w-full">
                                    <CaretDropdown
                                        open={open}
                                        flattenBaseOnOpen
                                        className="border-S2D-neutral-10 bg-S2D-neutral-100"
                                        content={
                                            <div className="pl-25px flex items-center gap-2 p-3">
                                                {options[selectedTab - 1].icon}
                                                {options[selectedTab - 1].title}
                                            </div>
                                        }
                                    />
                                </Menu.Button>
                                {open && (
                                    <Menu.Items
                                        className={`rounded rounded-t-none border-2 border-t-0 border-S2D-neutral-10`}
                                        static
                                    >
                                        {options.map((option) => {
                                            const itemIsSelected =
                                                option.title ===
                                                options[selectedTab - 1].title;

                                            return (
                                                <Menu.Item key={option.id}>
                                                    {({ active }) => (
                                                        <div
                                                            onClick={() =>
                                                                handleSelect(
                                                                    option.id
                                                                )
                                                            }
                                                            className={`flex items-center gap-2 p-3 text-[16px] font-semibold ${
                                                                itemIsSelected
                                                                    ? "bg-S2D-light-green-80"
                                                                    : ""
                                                            } ${
                                                                active
                                                                    ? "bg-S2D-neutral-90"
                                                                    : ""
                                                            }`}
                                                        >
                                                            {option.icon}
                                                            {option.title}
                                                        </div>
                                                    )}
                                                </Menu.Item>
                                            );
                                        })}
                                    </Menu.Items>
                                )}
                            </>
                        )}
                    </Menu>
                </>
            )}

            <div className="pt-[20px]">
                <DashboardAlertsTabContents
                    alertsData={getDashboardTabData(selectedTab, alertsData)}
                    isLoadingAlerts={isLoading}
                    action={getDashboardTabActions(selectedTab, actions)}
                />
            </div>
        </div>
    );
}

export default DropDownContentSelector;
